/**
 * AppSidebar — React port of AngularJS SidebarController + sidebar.html
 *
 * AngularJS source:
 *   Controller: public/layout/sidebar.js (750 LOC)
 *   Template:   public/layout/sidebar.html (681 LOC)
 *
 * The sidebar is context-sensitive with 8 main states:
 *   - 'property' / 'CTD' — property info panel
 *   - 'workorder' — work order detail panel
 *   - 'audit' — audit context panel with vendor/property info
 *   - 'auditresults' — audit results panel with counts/categories
 *   - 'companyDetails' / 'companyMaintenance' / 'serviceArea' — VM company sidebar
 *   - 'clientpricing' — pricing back link
 *   - 'caseManagementManager' — case-management manager configuration links
 *   - '' / 'admin' / 'overallowables' / 'vendorManagement' — empty (hidden)
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { useSiteContextStore } from './useSiteContextStore';
import { usePropertyStore } from '@/store/propertyStore';
import type { WorkOrder, GProperty } from '@/store/propertyStore';
import {
  useFinancialWorkflow,
  useVendorWorkflow,
  useAcceptAllSourceSystems,
  useCompanyDetails,
  useWds,
  useWdsStore,
  useWorkOrderImageCounts,
  useRctStore,
  useUIStore,
  useAppGlobalsStore,
} from '@/store';
import { useLegacyPermissions } from '@/lib/useLegacyPermissions';
import { broadcastAngularEvent } from '@/lib/broadcastAngularEvent';
import { getPermission, validDynamic } from '@/lib/permissionsApi';
import { obfuscateTextByPermission } from '@/lib/obfuscate';
import { useModalStore } from '@/store/modalStore';
import { completeOrder } from '@/lib/workflowApi';
import { replaceGlobalOrder } from '@/lib/replaceGlobalOrder';
import { navigateToLegacyVendorRoute } from '@/lib/legacyNavigation';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { formatDate } from '@/lib/formatters';
import { buildApiBase } from '@/lib/buildApiBase';
import { axiosClient } from '@/lib/axiosClient';
import {
  getPropertyQuestionAnswers,
  getWorkOrderQuestionAnswersByModule,
  getWDSOrder,
  listAssets,
  getClientAttributeLuggage,
  getWOLuggage,
  type AssetUnit,
  type PropertyQuestionAnswer,
} from '@/features/auditDashboard/auditApi';
import { getRCTAlwaysEnableKey } from '@/lib/webconfigApi';
import { useCountService, type CountServiceState } from '@/features/auditDashboard/useCountService';
import { useAuditStore } from '@/features/auditDashboard/useAuditStore';
import {
  getManagerConfigurationSectionFromSearch,
  managerConfigurationSidebarLinks,
} from '@/features/caseManagement/pages/managerConfigurationNavigation';

// ============ Sidebar-local shared state ============

/**
 * Mirrors AngularJS SidebarController `vm.wdsAction` (sidebar.js:625 — set by
 * the shift options dropdown, read by determineRedirectAndNextOrder when
 * calling auditDataService.GetWDSOrder, sidebar.js:114). Sidebar-local share
 * between `AuditShiftOptions` (writer) and `AuditCompleteButton` (reader).
 */
const useSidebarShiftAction = create<{
  wdsAction: string;
  setWdsAction: (v: string) => void;
}>((set) => ({
  wdsAction: 'Continue',
  setWdsAction: (v) => set({ wdsAction: v }),
}));

/**
 * Port of AngularJS SidebarController `$scope.reloadAuditResults`. Module-level
 * so both the parent `AppSidebar` and the `AuditRCTButton` (a sibling component)
 * can trigger the audit-results reload after an RCT call-out.
 */
function reloadAuditResults(): void {
  broadcastAngularEvent('AUDIT_RESULTS_DATA_CORESCRIPT', { resetForce: true });
  // React listeners use the window CustomEvent mirror (audit-results-data-corescript).
  window.dispatchEvent(
    new CustomEvent('audit-results-data-corescript', { detail: { resetForce: true } })
  );
  window.location.hash = '#/Audit/auditdashboard/results';
}

// ============ RCT visibility determination ============

/**
 * Work-order states in which the RCT Call Out flow is never shown. Exact port
 * of the `invalidStates` array in AngularJS getPropertyAssets (sidebar.js:387).
 */
const RCT_INVALID_STATES = [
  'auditconfirmed',
  'auditcompletepending',
  'audited',
  'multipleopen',
  'billablecancellation',
  'canceled',
];

/**
 * Port of AngularJS SidebarController `getClientAttributeValues()`
 * (sidebar.js:565-611). Determines whether the RCT Call Out panel is shown for
 * the current work order by inspecting client attribute luggage + survey
 * answers, and reads the `rctAlwaysEnable` web.config flag.
 *
 * Fire-and-forget nesting is preserved 1:1: `doNotShowRct` is set to `false`
 * immediately when CoreLogicRCTIntegration is enabled, and the RCTIntegrationType
 * / survey checks may later flip it back to `true` when their promises resolve —
 * exactly as the AngularJS `.then` callbacks do (they are not awaited before the
 * enclosing function returns).
 */
async function runRctDetermination(workOrder: SidebarWorkOrder, isSGStaff: boolean): Promise<void> {
  const rct = useRctStore.getState();
  const plId = (workOrder as { propertyLoanId?: number }).propertyLoanId;
  const orderNumber = Number(workOrder.orderNumber);

  // sidebar.js:567 — RCTStateClean is reset at the start of the determination.
  rct.setRctStateClean(false);

  if (!isSGStaff || plId == null) return;

  try {
    const response = await getClientAttributeLuggage('CoreLogicRCTIntegration', plId, orderNumber);

    // sidebar.js:571-572 — reset both flags before evaluating the response.
    useRctStore.getState().setDoNotShowRct(true);
    useRctStore.getState().setRctAlwaysEnable(false);

    if (response && response.length > 0) {
      if (response[0] === 'Disabled') {
        useRctStore.getState().setDoNotShowRct(true);
      } else {
        useRctStore.getState().setDoNotShowRct(false);

        // verify fit (sidebar.js:582-606) — fire-and-forget, may re-hide.
        getClientAttributeLuggage('RCTIntegrationType', plId, orderNumber).then((rctType) => {
          if (rctType && rctType.length > 0) {
            if (rctType[0] === 'FIT') {
              getWOLuggage(plId, orderNumber).then((data) => {
                const corelogicFit = data?.questions?.['WorkOrder.CoreLogic.Fit'];
                const ableToComplete = data?.questions?.['WorkOrder.Hanover.Abletocomplete'];
                if (
                  corelogicFit == null ||
                  (corelogicFit.answers.length > 0 &&
                    corelogicFit.answers[0].value !== 'Default.Yes') ||
                  ableToComplete == null ||
                  (ableToComplete.answers.length > 0 &&
                    ableToComplete.answers[0].value === 'Default.No')
                ) {
                  useRctStore.getState().setDoNotShowRct(true);
                }
              });
            } else if (rctType[0] === '360Value' || rctType[0] === 'FIT-Farmers') {
              getWOLuggage(plId, orderNumber).then((data) => {
                const completeSurvey = data?.questions?.['WorkOrder.INSSRVC.CompleteSurvey'];
                if (
                  completeSurvey == null ||
                  (completeSurvey.answers.length > 0 &&
                    completeSurvey.answers[0].value === 'Default.No')
                ) {
                  useRctStore.getState().setDoNotShowRct(true);
                }
              });
            }
          }
        });

        getRCTAlwaysEnableKey().then((data) => {
          useRctStore.getState().setRctAlwaysEnable(data);
        });
      }
    }
  } catch {
    // graceful degradation — leave doNotShowRct at its safe default (true).
  }
}

// ============ Extended Types (sidebar needs more fields than the store types) ============

/** Extended WorkOrder with all fields the sidebar renders */
interface SidebarWorkOrder extends WorkOrder {
  openDate?: string;
  dueDate?: string;
  vendorCode?: string;
  updater?: string;
  status?: string;
  internalInstructions?: string;
  specialInstructions?: string;
  clientOrderNumber?: string;
  reassignedDate?: string;
  vendorSubmitDate?: string;
  vendorCompleteDate?: string;
  latestVendorUpdateURL?: string;
}

/** Extended GProperty with all fields the sidebar renders */
interface SidebarProperty extends GProperty {
  occupancy?: string;
  ftvDate?: string;
  secureDate?: string;
  conveyDate?: string;
  specialInstructions?: string;
}

/** Flattened Q&A pair for sidebar display */
interface SidebarQA {
  question: string;
  answer: string;
}

/** Work order question answer item from API */
interface WOQuestionAnswer {
  questionText: string;
  questionKey: string;
  answerValues: string[];
  questionType?: string;
}

/** Vendor info from ServiceAreaSearch */
interface VendorInfo {
  companyViewModel?: {
    company?: {
      ownerName?: string;
      email?: string;
    };
  };
  companies?: {
    company?: {
      serviceAreas?: {
        contacts?: {
          phoneBusiness?: string;
        }[];
      }[];
    };
  }[];
}

// ============ Helper: format date or fallback ============

function fmtDate(value: string | undefined | null): string {
  if (!value) return 'N/A';
  return formatDate(value) || 'N/A';
}

// ============ Helper: format phone ============

function fmtPhone(phone: string | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

// ============ Sidebar Module Selector (matches AngularJS selectModuleByContext) ============

function selectModuleByContext(context: string): 'propertysidebar' | 'auditSidebar' | null {
  switch (context) {
    case 'workorder':
    case 'property':
      return 'propertysidebar';
    case 'audit':
      return 'auditSidebar';
    default:
      return null;
  }
}

// ============ Main Component ============

export function AppSidebar() {
  const currentContext = useSiteContextStore((s) => s.currentContext);
  const workOrder = usePropertyStore((s) => s.workOrder) as SidebarWorkOrder | null;
  const property = usePropertyStore((s) => s.property) as SidebarProperty | null;
  const permissions = useLegacyPermissions();
  const { copy } = useCopyToClipboard();
  const countService = useCountService();
  const { showSuccess, showError } = useUIStore();
  // `wdsAction` state was previously lifted here for prop-passing; the
  // dedicated `useSidebarShiftAction` Zustand store (declared above) now owns
  // it and both `AuditShiftOptions` and `AuditCompleteButton` read from there
  // directly, so this component no longer needs to plumb it.

  // ── Local state ──
  const [sidebarData, setSidebarData] = useState<SidebarQA[] | null>(null);
  const [sidebarWorkOrderData, setSidebarWorkOrderData] = useState<WOQuestionAnswer[]>([]);
  const [auditSidebarData, setAuditSidebarData] = useState<WOQuestionAnswer[]>([]);
  // Refresh-icon visibility — derived from the URL exactly the way AngularJS does it.
  //
  // AngularJS source: deleted/public/index.js:361-376 — a $routeChangeSuccess
  // handler writes `$rootScope.showRefreshBtn` based ONLY on whether the last
  // URL segment contains "results" or "workOrderWorkFlowHistory". The
  // `ng-show="showRefreshBtn"` bindings in sidebar.html read that rootScope
  // value via scope inheritance. The vm.showRefreshBtn updates inside
  // sidebar.js and the SHOW_REFRESH_ON_SIDEBAR_WO_WF_H broadcasts from the
  // WF-history controllers are effectively dead code — they update a
  // different binding the template never reads.
  //
  // So:
  //   /Audit/auditdashboard/results                     → VISIBLE
  //   /Audit/auditdashboard/workOrderWorkFlowHistory    → VISIBLE
  //   /Audit/auditdashboard/orderStatusTracker          → HIDDEN
  //   /Audit/auditdashboard/workOrderFinancialWorkFlowHistory → HIDDEN
  //     (the "Financial" segment breaks the "workOrderWorkFlowHistory"
  //      substring match — quirky but it's what AngularJS does)
  const location = useLocation();
  const showRefreshBtn = useMemo(() => {
    // AngularJS reads $location.$$url (path + query) then takes the substring
    // after the last "/". Mirror that with pathname + search so query strings
    // like ?workorder=500112707 are included in the match window.
    const url = location.pathname + location.search;
    const lastSlash = url.lastIndexOf('/');
    const tail = url.substring(lastSlash + 1);
    return tail.indexOf('results') >= 0 || tail.indexOf('workOrderWorkFlowHistory') >= 0;
  }, [location.pathname, location.search]);
  const managerConfigurationSection = useMemo(
    () => getManagerConfigurationSectionFromSearch(location.search),
    [location.search]
  );
  const isManagerConfigurationPath = location.pathname
    .toLowerCase()
    .endsWith('/managerconfiguration');
  const isManagerOverviewPath = location.pathname.toLowerCase().endsWith('/manageroverview');
  const [showVendorInfo, setShowVendorInfo] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<VendorInfo | null>(null);
  const [stData, setStData] = useState('');
  const [applySTLogic, setApplySTLogic] = useState(false);

  // Financial / vendor workflow from app globals store
  const financialWorkflow = useFinancialWorkflow();
  const vendorWorkflow = useVendorWorkflow();

  // Company details from company store (for VM sidebar)
  const companyDetailsRaw = useCompanyDetails();
  const companyDetails = companyDetailsRaw as Record<string, any> | null;

  // EIN display permission
  const [displayEIN, setDisplayEIN] = useState(false);

  // ── Audit-specific state ──
  // Pull live billing totals from the audit store — matches AngularJS sidebar.html
  // `C: {{vm.clientTotal | currency}} : V: {{vm.vendorTotal | currency}}`, where
  // those scope values come from auditDataService's getClientAndVendorTotals().
  const billingTotals = useAuditStore((s) => s.billingTotals);
  const clientTotal = billingTotals.clientTotal;
  const vendorTotal = billingTotals.vendorTotal;
  const [, setTaskFailures] = useState<unknown[]>([]);

  // ── Helpers ──

  const orderNumber = workOrder?.orderNumber;
  const propertyId = property?.id;
  // During route transitions the `property` store can briefly point at the
  // previous selection while `workOrder` has already swapped to the new one
  // — e.g. clicking "To Work Order" from a /Property/record page. Showing
  // the old property's loan / address / etc. in the work-order sidebar is
  // worse than showing nothing, so the work-order section gates its
  // property-derived fields on this match.
  //
  // Linking key: `property.id` is the same numeric key that the work order
  // carries as `propertyLoanId` (see the `usePropertyLoanId` selector in
  // propertyStore.ts) — so that's what we compare.
  //
  // If the work order doesn't carry a `propertyLoanId` (older payloads,
  // partially-loaded work orders) the gate stays permissive — we'd rather
  // show whatever `property` has than hide everything because the work
  // order is missing the cross-link.
  const propertyMatchesWorkOrder =
    !!property &&
    !!workOrder &&
    (workOrder.propertyLoanId == null || property.id === workOrder.propertyLoanId);

  // ── Open work order luggage in new tab ──
  const openWorkOrderLuggage = useCallback((on: number | string | undefined) => {
    if (!on) return;
    try {
      const goApiBase = buildApiBase('spiglassgoapi');
      window.open(`${goApiBase}/API/1.1/OrderSearchLite/SG/${on}`);
    } catch {
      // graceful degradation
    }
  }, []);

  // ── Open property lite luggage in new tab ──
  const openPropLiteLuggage = useCallback((plId: number | undefined) => {
    if (!plId) return;
    try {
      const goApiBase = buildApiBase('spiglassgoapi');
      window.open(`${goApiBase}/API/1.1/PropertyLoanLite/SG/${plId}`);
    } catch {
      // graceful degradation
    }
  }, []);

  // ── Navigate to overview (matches $scope.toOverview) ──
  const toOverview = useCallback(() => {
    const wds = useWdsStore.getState().wds as Record<string, any> | null;
    if (wds?.isWds && wds.isWds) {
      window.location.hash = `#/Audit/auditdashboard/overview?ItemTypeRecordId=${wds.itemTypeRecordId}&Iswds=${wds.isWds}&CurrentQueue=${wds.currentWDSQueue}&ItemType=${wds.itemType}`;
    } else if (workOrder?.orderNumber) {
      window.location.hash = `#/Audit/auditdashboard/orderStatusTracker?workOrder=${workOrder.orderNumber}&forceOrderStatusTracker=true`;
    }
  }, [workOrder?.orderNumber]);

  // ── Show vendor update (matches $scope.showVendorUpdate) ──
  const showVendorUpdate = useCallback(() => {
    if (workOrder?.latestVendorUpdateURL) {
      window.open(workOrder.latestVendorUpdateURL, '_blank');
    }
  }, [workOrder?.latestVendorUpdateURL]);

  // ── Get company service area info (matches $scope.getCompanyServiceAreaInfo) ──
  const getCompanyServiceAreaInfo = useCallback(
    async (code: string) => {
      setShowVendorInfo((prev) => !prev);
      if (!companyInfo) {
        try {
          const vmApiBase = buildApiBase('vmapi');
          const response = await axiosClient.get(
            `${vmApiBase}/API/VendorManagement/ServiceAreaSearch`,
            {
              params: { serviceArea: code },
            }
          );
          setCompanyInfo(response.data);
        } catch {
          // graceful degradation
        }
      }
    },
    [companyInfo]
  );

  // ── Flatten property question answers (matches flattenPropertyQuestionAnswers) ──
  const flattenPropertyQuestionAnswers = useCallback(
    (assetData: PropertyQuestionAnswer | null): SidebarQA[] => {
      if (!assetData?.propertyQuestionAnswers) return [];
      const flat: SidebarQA[] = [];

      for (const pqa of assetData.propertyQuestionAnswers) {
        // Skip "Group" type questions
        if (pqa.questionType === 'Group') continue;

        // "Investor Number" -> "Investor #" (matches AngularJS string replace)
        const questionText =
          pqa.questionText === 'Investor Number' ? 'Investor #' : pqa.questionText;

        let answer = (pqa as any).answerValues?.[0] ?? '';

        // Format date-type answers
        if (answer && pqa.questionType === 'Date') {
          answer = formatDate(answer) || answer;
        }

        // Special handling: set specialInstructions on G_PROPERTY
        if ((pqa as any).questionKey === 'Property.PropertySpecialInstruction') {
          // Matches flattenPropertyQuestionAnswersAddToGProperty
          // We don't mutate $rootScope here; the AngularJS controller handles it
          continue;
        }

        flat.push({ question: questionText, answer: answer || 'N/A' });
      }

      return flat;
    },
    []
  );

  // ── Load property Q&A data (matches getPropertyQuestionAnswers) ──
  const loadPropertyQA = useCallback(
    async (context: string) => {
      // STALE-DATA FIX — reset BEFORE the early-returns AND before the
      // network call. The previous version only cleared on a falsy
      // propertyId; if the new property had no matching DEFAULT asset
      // (or empty Q&A), the success branch never updated state and the
      // sidebar kept rendering the PREVIOUS property's question/answer
      // rows. Same pattern as the imageCounts / workOrder clear in
      // PropertyRecordRoute.activate.
      setSidebarData(null);

      if (!propertyId) return;

      const sidebarModule = selectModuleByContext(context);
      if (!sidebarModule) return;
      if (!permissions.searchEnabled) return;

      try {
        const data = await getPropertyQuestionAnswers(propertyId, null, null, null, sidebarModule);

        if (data && data.length > 0) {
          // Find DEFAULT asset unit
          const assets = await listAssets(propertyId, null);
          const defaultUnit = assets?.find((u: AssetUnit) => u.id && u.assetName === 'DEFAULT');

          if (defaultUnit) {
            const match = data.find((d: any) => d.assetId === defaultUnit.id);
            if (match) {
              setSidebarData(flattenPropertyQuestionAnswers(match));
            }
          }
        }
      } catch {
        setSidebarData(null);
      }
    },
    [propertyId, permissions.searchEnabled, flattenPropertyQuestionAnswers]
  );

  // ── Load work order sidebar data (matches getWorkOrderSidebarData) ──
  const loadWorkOrderSidebarData = useCallback(async () => {
    // STALE-DATA FIX — clear before the early-return so a missing
    // propertyId/orderNumber AND a new WO that returns no rows both
    // result in an empty sidebar, not the previous order's rows.
    setSidebarWorkOrderData([]);
    if (!propertyId || !orderNumber) return;
    try {
      const data = await getWorkOrderQuestionAnswersByModule(
        Number(orderNumber),
        'workorderleftsidebar'
      );
      if (data && data.length > 0) {
        setSidebarWorkOrderData(data[0].propertyQuestionAnswers || []);
      }
    } catch {
      // graceful degradation — sidebar shows the empty state set above
    }
  }, [propertyId, orderNumber]);

  // ── Load audit sidebar data (matches getAuditSidebarData) ──
  const loadAuditSidebarData = useCallback(async () => {
    // Same stale-data fix as loadWorkOrderSidebarData.
    setAuditSidebarData([]);
    if (!propertyId || !orderNumber) return;
    try {
      const data = await getWorkOrderQuestionAnswersByModule(
        Number(orderNumber),
        'auditLeftsidebar'
      );
      if (data && data.length > 0) {
        setAuditSidebarData(data[0].propertyQuestionAnswers || []);
      }
    } catch {
      // graceful degradation
    }
  }, [propertyId, orderNumber]);

  // ── Load ST data (matches getSTData) ──
  const loadSTData = useCallback(async () => {
    if (!propertyId || !orderNumber) {
      setStData('');
      return;
    }
    try {
      const goApiBase = buildApiBase('spiglassgoapi');
      const response = await axiosClient.get(
        `${goApiBase}/API/1.1/PropertyLoanLite/SG/${propertyId}/${orderNumber}`,
        { silent401: true }
      );
      const stNumberDetails = response.data?.questions?.['WorkOrder.STNumberDetails'];
      if (stNumberDetails?.answers?.length > 0) {
        setStData(stNumberDetails.answers[0].value || '');
      } else {
        setStData('');
      }
    } catch {
      setStData('');
    }
  }, [propertyId, orderNumber]);

  // ── Load WO attribute data (matches getWOAttributeData) ──
  const loadWOAttributeData = useCallback(async () => {
    // Mirror to global store so ST-consuming routes (BidDisposition/AddBid/...) react.
    const setGlobal = useAppGlobalsStore.getState().setApplySTLogic;
    const apply = (v: boolean) => {
      setApplySTLogic(v);
      setGlobal(v);
    };
    if (!property || !orderNumber) {
      apply(false);
      return;
    }
    try {
      const apiBase = buildApiBase();
      const clientCode =
        typeof property.clientCode === 'object' ? property.clientCode?.code : property.clientCode;
      const response = await axiosClient.get(`${apiBase}/API/Client/GetWorkOrderAttributeV3`, {
        params: {
          attributeName: 'ApplySTNumber',
          clientCode: clientCode,
          workCode: workOrder?.workCode,
          department: workOrder?.department,
        },
        silent401: true,
      });
      apply(response.data?.length > 0 && response.data[0] === 'Yes');
    } catch {
      apply(false);
    }
  }, [property, orderNumber, workOrder?.workCode, workOrder?.department]);

  // ── Check EIN display permission (matches obfuscateEINSSNNumber) ──
  useEffect(() => {
    setDisplayEIN(getPermission('Vendor:ViewEIN').validPermission);
  }, []);

  // ── Financial/vendor workflow + company details now read reactively from stores ──

  // ── Main effect: update sidebar data when context changes ──
  useEffect(() => {
    if (!currentContext) return;

    setShowVendorInfo(false);
    setCompanyInfo(null);

    loadPropertyQA(currentContext);

    if (currentContext === 'workorder' || currentContext === 'auditresults') {
      loadWorkOrderSidebarData();
      loadAuditSidebarData();
    }

    // Refresh button visibility is now derived from the URL above (matches
    // AngularJS index.js:361-376). The sidebar.js:194-198 logic that flipped
    // vm.showRefreshBtn based on context never actually drove the icon — the
    // template reads $rootScope.showRefreshBtn, which only index.js sets.

    loadSTData();
    loadWOAttributeData();

    // ── RCT visibility determination ──
    // Port of AngularJS getPropertyAssets (sidebar.js:388-411): gated on
    // searchEnabled, run getClientAttributeValues only when the work order is in
    // a valid state; otherwise force-hide RCT. The RCT branch there lives inside
    // the ListAssets callback but depends only on G_WORKORDER, so it lifts here.
    if (permissions.searchEnabled && workOrder) {
      const stateCode = workOrder.workflowStateTransition?.currentState?.code;
      if (
        workOrder.workflowStateTransition &&
        stateCode &&
        !RCT_INVALID_STATES.includes(stateCode.toLowerCase())
      ) {
        runRctDetermination(workOrder, Boolean(permissions.SGStaff));
      } else {
        useRctStore.getState().setDoNotShowRct(true);
      }
    }
  }, [
    currentContext,
    permissions.searchEnabled,
    permissions.SGStaff,
    workOrder,
    // Re-fetch when the actual work order / property changes, not just when
    // the sidebar context type does. Without these, navigating between two
    // orders in the same context (e.g. two audit orders via the search
    // widget or a Next button) leaves the sidebar showing data from the
    // first order even after the store has swapped.
    orderNumber,
    propertyId,
    loadPropertyQA,
    loadWorkOrderSidebarData,
    loadAuditSidebarData,
    loadSTData,
    loadWOAttributeData,
  ]);

  // ── Event listeners (AUDIT_BILLING_TASKSFAILURES, SET_SHIFT_OPTIONS) ──
  //
  // SHOW_REFRESH_ON_SIDEBAR_WO_WF_H is intentionally NOT listened for here:
  // refresh-icon visibility is derived from the URL (see showRefreshBtn
  // useMemo above), mirroring AngularJS index.js:361-376. The legacy event
  // broadcasts from workOrderWorkFlowHistory.js / workOrderFinancialWorkFlowHistory.js
  // updated `vm.showRefreshBtn`, which the templates never read.
  useEffect(() => {
    const handleTaskFailures = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setTaskFailures(detail?.data ?? []);
    };
    const handleShiftOptions = () => {
      // checkShiftOptions is handled in the audit results section
    };

    window.addEventListener('AUDIT_BILLING_TASKSFAILURES', handleTaskFailures);
    window.addEventListener('SET_SHIFT_OPTIONS', handleShiftOptions);

    return () => {
      window.removeEventListener('AUDIT_BILLING_TASKSFAILURES', handleTaskFailures);
      window.removeEventListener('SET_SHIFT_OPTIONS', handleShiftOptions);
    };
  }, []);

  // ── Reload work order history (matches $scope.reloadWorkOrderHistory) ──
  const reloadWorkOrderHistory = useCallback(() => {
    broadcastAngularEvent('RELOAD_WO_WF_H');
    window.dispatchEvent(new CustomEvent('audit-reload-wo-wf-h'));
  }, []);

  // ── Status class helper (matches ng-class for open/billed/voided) ──
  const getStatusClass = useCallback((status: string | undefined): string => {
    switch (status) {
      case 'NEW':
        return 'text-angular-green';
      case 'LOCK_AUDIT':
        return 'text-angular-yellow';
      case 'CLOSED':
        return 'text-angular-red';
      default:
        return '';
    }
  }, []);

  // ── WO Question answer display filter (matches InPersonInspectionOnly logic) ──
  const shouldShowWOQuestion = useCallback((q: WOQuestionAnswer): boolean => {
    if (!q.answerValues || q.answerValues.length === 0) return false;
    if (q.questionKey === 'WorkOrder.InPersonInspectionOnly' && q.answerValues[0] !== 'Yes') {
      return false;
    }
    return true;
  }, []);

  // ── Internal instructions display ──
  const renderInternalInstructions = useCallback((instructions: string | undefined) => {
    if (!instructions) return <span>N/A</span>;
    if (instructions.length < 14) {
      return <span>{instructions}</span>;
    }
    return <div style={{ textAlign: 'center', wordWrap: 'normal' }}>{instructions}</div>;
  }, []);

  // ── Special instructions display ──
  const renderSpecialInstructions = useCallback(
    (label: string, instructions: string | undefined) => {
      if (!instructions) return null;
      return (
        <li>
          <span>{label}:</span>
          <div style={{ textAlign: 'center', wordWrap: 'normal' }}>{instructions}</div>
        </li>
      );
    },
    []
  );

  // ── ST Data display ──
  const renderSTData = useCallback(() => {
    if (!stData || !applySTLogic) return null;
    return (
      <li className='cursor-pointer' onClick={() => copy(stData)}>
        <span>ST# Details:</span>
        <div style={{ textAlign: 'center', wordWrap: 'normal' }}>{stData}</div>
      </li>
    );
  }, [stData, applySTLogic, copy]);

  // ── Financial/Vendor workflow display ──
  const renderFinancialWorkflow = useCallback(() => {
    if (!financialWorkflow) return null;
    return (
      <li>
        <span className='label' title='Invoice Status'>
          Invoice Status:{' '}
        </span>
        {financialWorkflow.displayStatus}
      </li>
    );
  }, [financialWorkflow]);

  const renderVendorWorkflow = useCallback(() => {
    if (!vendorWorkflow) return null;
    return (
      <li>
        <span className='label' title='Invoice Status'>
          Vendor Financial Status:{' '}
        </span>{' '}
        &nbsp;&nbsp; {vendorWorkflow.name}
      </li>
    );
  }, [vendorWorkflow]);

  // ============================================================================
  // RENDER: Empty states — admin, overallowables, vendorManagement, empty
  // ============================================================================

  if (currentContext === 'caseManagementManager') {
    if (!permissions.canManageComplaintRoutingRules) {
      return <div className='property' />;
    }

    return (
      <div className='property'>
        <div className='sidebar-inner'>
          <div className='sidebar-header'>
            <h4>Case Management</h4>
          </div>
          <ul className='nav nav-sidebar'>
            {managerConfigurationSidebarLinks.map((link) => {
              const isActive =
                link.key === 'overview'
                  ? isManagerOverviewPath
                  : isManagerConfigurationPath && link.key === managerConfigurationSection;

              return (
                <li key={link.key}>
                  <a
                    href={`#${link.path}`}
                    aria-current={isActive ? 'page' : undefined}
                    className={
                      isActive ? 'case-management-manager-sidebar-link--active' : undefined
                    }
                  >
                    {link.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  // Matches sidebar.html line 2:
  //   <div class="property" ng-if="vm.sidebar === '' || vm.sidebar === 'overallowables'
  //        || vm.sidebar==='vendorManagement' || vm.sidebar === 'admin'"></div>
  // The outer Bootstrap col-md-2 sidebar wrapper is ALWAYS rendered (index.html:57),
  // so these contexts still get the dark sidebar column — just empty.
  if (
    !currentContext ||
    currentContext === '' ||
    currentContext === 'overallowables' ||
    currentContext === 'vendorManagement' ||
    currentContext === 'admin'
  ) {
    return <div className='property' />;
  }

  // ============================================================================
  // RENDER: Property / CTD context
  // ============================================================================

  if (currentContext === 'property' || currentContext === 'CTD') {
    return (
      <div className='property'>
        <div className='sidebar-inner'>
          <div className='sidebar-header'>
            <h5>
              Loan #:{' '}
              <span style={{ textTransform: 'none' }}>
                {obfuscateTextByPermission(
                  String(property?.loanNumber ?? ''),
                  'Audit:ViewLoanNumber'
                )}
              </span>
            </h5>
            <h5>
              <span
                className='cursor-pointer'
                onClick={() => property?.spiPropertyId && copy(String(property.spiPropertyId))}
              >
                Property ID: {property?.spiPropertyId}
              </span>
            </h5>
            {property?.active === 'N' && <h5>Inactive</h5>}
            {permissions.isSupport && (
              <h5>
                <span
                  className='cursor-pointer'
                  onClick={() => propertyId && copy(String(propertyId))}
                >
                  Prop Loan ID: {propertyId}{' '}
                </span>
                <i
                  className='fa fa-external-link cursor-pointer'
                  onClick={() => openPropLiteLuggage(propertyId)}
                />
              </h5>
            )}
          </div>
          <ul className='nav nav-sidebar'>
            {permissions.auditViewPropertyAddresses && (
              <li>
                <div>
                  <div>{property?.address1}</div>
                  <div>
                    {property?.city}, {property?.state} {property?.zip}
                  </div>
                </div>
              </li>
            )}
            <li className='active'>
              <span className='label' title='Order #'>
                Order #:{' '}
              </span>
              {orderNumber ? (
                <span className='highlightOrderNumber'>
                  <a className='cursor-pointer' onClick={toOverview}>
                    {orderNumber}
                  </a>
                </span>
              ) : (
                <span>None Loaded</span>
              )}
            </li>
            {sidebarData?.map((qa, i) => (
              <li key={i}>
                <span className='label' title={qa.question}>
                  {qa.question}:{' '}
                </span>{' '}
                {qa.answer || 'N/A'}
              </li>
            ))}
          </ul>
          {orderNumber != null && (
            <a
              href={`#/Audit/auditdashboard/orderStatusTracker?workorder=${orderNumber}`}
              className='button white return-btn'
            >
              To Work Order
            </a>
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Work Order context
  // ============================================================================

  if (currentContext === 'workorder') {
    return (
      <div className='workorder'>
        <div className='sidebar-inner'>
          <div className='sidebar-header'>
            <h4>
              Work Order
              {showRefreshBtn && (
                // AngularJS used <img class="refreshButton"> with no src — the
                // icon comes from main.css:4100 `background-image`. A previous
                // attempt set src='/Content/images/icon-refresh.png' to avoid an
                // alt-text fallback, but that rendered the icon TWICE (once
                // from the <img>'s src, once from the CSS background). Use a
                // <span> instead: CSS background still renders, and there is no
                // image-load fallback to leak.
                <span
                  role='button'
                  aria-label='Refresh'
                  className='refreshButton cursor-pointer'
                  onClick={reloadWorkOrderHistory}
                />
              )}
            </h4>
            <h5>
              <span
                style={{ cursor: orderNumber ? 'pointer' : 'text' }}
                onClick={() => orderNumber && copy(String(orderNumber))}
              >
                Order #: {orderNumber || 'None Loaded'}{' '}
              </span>
              {permissions.viewWorkOrderLuggage && (
                <i
                  className='fa fa-external-link cursor-pointer'
                  onClick={() => openWorkOrderLuggage(orderNumber)}
                />
              )}
            </h5>
            <h5>
              Loan #:{' '}
              <span style={{ textTransform: 'none' }}>
                {propertyMatchesWorkOrder
                  ? obfuscateTextByPermission(
                      String(property?.loanNumber ?? ''),
                      'Audit:ViewLoanNumber'
                    )
                  : ''}
              </span>
            </h5>
            <h5>
              <span
                className='cursor-pointer'
                onClick={() =>
                  propertyMatchesWorkOrder &&
                  property?.spiPropertyId &&
                  copy(String(property.spiPropertyId))
                }
              >
                Property ID: {propertyMatchesWorkOrder ? property?.spiPropertyId : ''}
              </span>
            </h5>
            {propertyMatchesWorkOrder && property?.active === 'N' && <h5>Inactive</h5>}
            {permissions.isSupport && (
              <h5>
                <span
                  className='cursor-pointer'
                  onClick={() => propertyMatchesWorkOrder && propertyId && copy(String(propertyId))}
                >
                  Prop Loan ID: {propertyMatchesWorkOrder ? propertyId : ''}{' '}
                </span>
                {propertyMatchesWorkOrder && (
                  <i
                    className='fa fa-external-link cursor-pointer'
                    onClick={() => openPropLiteLuggage(propertyId)}
                  />
                )}
              </h5>
            )}
            <strong className='status temporary'>
              {workOrder?.workflowStateTransition?.currentState?.name}
            </strong>
            <ul className='nav nav-sidebar'>
              <li className='active'>
                <span className='label' title='Code'>
                  Code:{' '}
                </span>
                {workOrder?.workCode || 'N/A'}
              </li>
              <li>
                <span className='label' title='Updater'>
                  Updater:{' '}
                </span>
                {workOrder?.updater || 'N/A'}
              </li>
              <li>
                <span className='label' title='Images'>
                  Images:{' '}
                </span>
                <ImageCount />
              </li>
              <li>
                <span className='label' title='Opened'>
                  Opened:{' '}
                </span>
                {fmtDate(workOrder?.openDate)}
              </li>
              <li>
                <span className='label' title='Due'>
                  Due:{' '}
                </span>
                {fmtDate(workOrder?.dueDate)}
              </li>
              {workOrder?.cancellationDate && (
                <li style={{ backgroundColor: 'red' }}>
                  <span className='label' title='Cancel'>
                    Cancelled:{' '}
                  </span>
                  {fmtDate(workOrder.cancellationDate)}
                </li>
              )}
              <li>
                <span className='label' title='Reassigned'>
                  Reassigned:{' '}
                </span>
                {fmtDate(workOrder?.reassignedDate)}
              </li>
              {/* Work order question answers */}
              {sidebarWorkOrderData.filter(shouldShowWOQuestion).map((q, i) => (
                <li key={i}>
                  <span className='label' title={q.questionText}>
                    {q.questionText}:{' '}
                  </span>
                  <span>{q.answerValues[0]}</span>
                </li>
              ))}
              <li>
                <span className='label' title='Work Order Alerts'>
                  SPI WO Alert:
                </span>
                {renderInternalInstructions(workOrder?.internalInstructions)}
              </li>
              {renderSpecialInstructions(
                'Property Special Instructions',
                property?.specialInstructions
              )}
              {renderSpecialInstructions('Special Instructions', workOrder?.specialInstructions)}
              {renderSTData()}
              {renderFinancialWorkflow()}
              {renderVendorWorkflow()}
              {workOrder?.clientOrderNumber && (
                <li>
                  <span
                    className='label cursor-pointer'
                    title='Client Order Number'
                    onClick={() => copy(String(workOrder.clientOrderNumber))}
                  >
                    Client Order #:{' '}
                  </span>
                  <span
                    className='cursor-pointer'
                    onClick={() => copy(String(workOrder.clientOrderNumber))}
                  >
                    {workOrder.clientOrderNumber}
                  </span>
                </li>
              )}
            </ul>

            {/* VENDOR INFORMATION */}
            <h6>VENDOR INFORMATION</h6>
            <ul className='nav nav-sidebar'>
              <li className='active'>
                <span className='label sidebarlispan' title='Code'>
                  Code:{' '}
                  {workOrder?.vendorCode !== 'SPI' && (
                    <i
                      className='fa fa-caret-down pull-right cursor-pointer'
                      onClick={() =>
                        workOrder?.vendorCode && getCompanyServiceAreaInfo(workOrder.vendorCode)
                      }
                    />
                  )}
                  {workOrder?.vendorCode !== 'SPI' ? (
                    <a
                      className='linkname pull-right underline-vendor-code'
                      href={`#/VendorManagement/ServiceArea/Update?Search=${workOrder?.vendorCode}&SearchBy=Service Area`}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {workOrder?.vendorCode}
                    </a>
                  ) : (
                    <a className='linkname pull-right'>{workOrder?.vendorCode}</a>
                  )}
                </span>
                {showVendorInfo && companyInfo && (
                  <div>
                    <span className='label'>
                      {companyInfo.companyViewModel?.company?.ownerName}
                    </span>
                    <span className='label'>
                      {fmtPhone(
                        companyInfo.companies?.[0]?.company?.serviceAreas?.[0]?.contacts?.[0]
                          ?.phoneBusiness
                      )}
                    </span>
                    <span className='linkyellow'>
                      <a
                        href={`mailto:${companyInfo.companyViewModel?.company?.email}`}
                        className='linkname'
                      >
                        {companyInfo.companyViewModel?.company?.email}
                      </a>
                    </span>
                  </div>
                )}
              </li>
              <li>
                <span className='label' title='Submit'>
                  Submit:{' '}
                </span>
                {fmtDate(workOrder?.vendorSubmitDate)}
              </li>
              <li>
                <span className='label' title='Complete'>
                  Complete:{' '}
                </span>
                {fmtDate(workOrder?.vendorCompleteDate)}
              </li>
            </ul>

            {/* PROPERTY INFORMATION */}
            <h6>PROPERTY INFORMATION</h6>
            <ul className='nav nav-sidebar'>
              {permissions.auditViewPropertyAddresses && (
                <li>
                  <div>
                    <div>{property?.address1}</div>
                    <div>
                      {property?.city}, {property?.state} {property?.zip}
                    </div>
                  </div>
                </li>
              )}
              {sidebarData?.map((qa, i) => (
                <li key={i}>
                  <span className='label' title={qa.question}>
                    {qa.question}:{' '}
                  </span>{' '}
                  {qa.answer || 'N/A'}
                </li>
              ))}
            </ul>
          </div>
          {propertyId && (
            <a href='#/Property/record' className='button white return-btn'>
              Property Record
            </a>
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Audit context
  // ============================================================================

  if (currentContext === 'audit') {
    return (
      <div className='audit'>
        <div className='sidebar-inner'>
          <div className='sidebar-header'>
            <h5>
              <span
                style={{ cursor: orderNumber ? 'pointer' : 'text' }}
                onClick={() => orderNumber && copy(String(orderNumber))}
              >
                Order #: {orderNumber || 'None Loaded'}{' '}
              </span>
              {permissions.viewWorkOrderLuggage && (
                <i
                  className='fa fa-external-link cursor-pointer'
                  onClick={() => openWorkOrderLuggage(orderNumber)}
                />
              )}
            </h5>
            <h5>
              <span
                className='cursor-pointer'
                onClick={() => property?.spiPropertyId && copy(String(property.spiPropertyId))}
              >
                Property ID: {property?.spiPropertyId}
              </span>
            </h5>
            {property?.active === 'N' && <h5>Inactive</h5>}
            {permissions.isSupport && (
              <h5>
                Prop Loan ID: {propertyId}{' '}
                <i
                  className='fa fa-external-link cursor-pointer'
                  onClick={() => openPropLiteLuggage(propertyId)}
                />
              </h5>
            )}
            <h5 className={`status ${getStatusClass(workOrder?.status)}`}>
              {workOrder?.workflowStateTransition?.currentState?.name}
            </h5>
            <ul className='nav nav-sidebar'>
              <li className='active'>
                <span className='label' title='Code'>
                  Code:{' '}
                </span>
                {workOrder?.workCode || 'N/A'}
              </li>
              <li>
                <span className='label' title='Updater'>
                  Updater:{' '}
                </span>
                {workOrder?.updater || 'N/A'}
              </li>
              <li>
                <span className='label' title='Opened'>
                  Opened:{' '}
                </span>
                {fmtDate(workOrder?.openDate)}
              </li>
              <li>
                <span className='label' title='Due'>
                  Due:{' '}
                </span>
                {fmtDate(workOrder?.dueDate)}
              </li>
              <li>
                <span className='label' title='Reassigned'>
                  Reassigned:{' '}
                </span>
                {fmtDate(workOrder?.reassignedDate)}
              </li>
              <li>
                <span className='label' title='Work Order Alerts'>
                  SPI WO Alert:
                </span>
                {renderInternalInstructions(workOrder?.internalInstructions)}
              </li>
              {renderSpecialInstructions(
                'Property Special Instructions',
                property?.specialInstructions
              )}
              {renderSpecialInstructions('Special Instructions', workOrder?.specialInstructions)}
              {renderSTData()}
              {renderFinancialWorkflow()}
              {renderVendorWorkflow()}
              {workOrder?.clientOrderNumber && (
                <li>
                  <span className='label' title='Client Order Number'>
                    Client Order #:{' '}
                  </span>
                  {workOrder.clientOrderNumber}
                </li>
              )}
            </ul>

            <h6>Vendor Information</h6>
            <ul className='nav nav-sidebar'>
              <li className='active'>
                <span className='label' title='Code'>
                  Code:{' '}
                </span>
                {workOrder?.vendorCode || 'N/A'}
              </li>
              <li>
                <span className='label' title='Submit'>
                  Submit:{' '}
                </span>
                {fmtDate(workOrder?.vendorSubmitDate)}
              </li>
              <li>
                <span className='label' title='Complete'>
                  Complete:{' '}
                </span>
                {fmtDate(workOrder?.vendorCompleteDate)}
              </li>
            </ul>

            <h6>Property Information</h6>
            {permissions.auditViewPropertyAddresses && (
              <li>
                <div>
                  <div>{property?.address1}</div>
                  <div>
                    {property?.city}, {property?.state} {property?.zip}
                  </div>
                </div>
              </li>
            )}
          </div>

          {/* Billing details trigger — matches AngularJS .button.white pill
              (1px white border, rounded, uppercase, transparent bg → white bg on hover
              with blue text). Inline styles + React state so no global CSS rule
              (e.g. main.css `.button:hover { color:#fff }` or any sidebar `button`
              selector) can hijack the hover color. */}
          <div className='pin-profile-group nav-sidebar'>
            <div className='flex justify-center'>
              <BillingTotalsButton clientTotal={clientTotal} vendorTotal={vendorTotal} />
            </div>
          </div>

          {/* Audit property details */}
          <div className='sidebar-header'>
            <ul className='nav nav-sidebar'>
              <li>
                <span className='label'>Occupancy: </span>
                {property?.occupancy || 'N/A'}
              </li>
              <li>
                <span className='label'>Sale: </span>
                {fmtDate(property?.saleDate)}
              </li>
              <li>
                <span className='label'>FTV: </span>
                {fmtDate(property?.ftvDate)}
              </li>
              <li>
                <span className='label'>Secured: </span>
                {fmtDate(property?.secureDate)}
              </li>
              <li>
                <span className='label'>Convey: </span>
                {fmtDate(property?.conveyDate)}
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Audit Results context
  // ============================================================================

  if (currentContext === 'auditresults') {
    return (
      // `sidebar` class restores the legacy CSS ancestor that
      // main.css:3312 (`.sidebar .profile-group .button[disabled] { opacity: .5 }`)
      // relies on. In AngularJS that ancestor came from <section class="col-md-2
      // sidebar"> in index.html:57; standalone React renders AppSidebar directly,
      // so without this class disabled Accept-All / Complete-Audit buttons would
      // not visually fade.
      <div className='sidebar auditresults auditsidebar'>
        <div className='sidebar-inner'>
          <div className='sidebar-group'>
            <h5>
              <span
                style={{ cursor: orderNumber ? 'pointer' : 'text' }}
                onClick={() => orderNumber && copy(String(orderNumber))}
              >
                Order #: {orderNumber || 'None Loaded'}{' '}
              </span>
              {permissions.viewWorkOrderLuggage && (
                <i
                  className='fa fa-external-link cursor-pointer'
                  onClick={() => openWorkOrderLuggage(orderNumber)}
                />
              )}
              {showRefreshBtn && (
                // Use <span> for the same reason as the workorder context above:
                // the icon comes from .refreshButton's CSS background-image, and
                // a <span> avoids the broken-image placeholder some browsers
                // render for <img> elements without a src.
                <span
                  role='button'
                  aria-label='Refresh'
                  className='refreshButton cursor-pointer'
                  onClick={reloadAuditResults}
                />
              )}
            </h5>
            <h5>
              <span
                className='cursor-pointer'
                onClick={() => property?.spiPropertyId && copy(String(property.spiPropertyId))}
              >
                Property ID: {property?.spiPropertyId}
              </span>
            </h5>
            <h5 className={`status ${getStatusClass(workOrder?.status)}`}>
              {workOrder?.workflowStateTransition?.currentState?.name}
            </h5>

            <ul className='nav nav-sidebar'>
              <li className='active'>
                <span className='label' title='Work Code'>
                  Work Code:{' '}
                </span>
                {workOrder?.workCode || 'N/A'}
              </li>
              <li>
                <span className='label' title='Loan Type'>
                  Loan Type:{' '}
                </span>
                {property?.loanType || 'N/A'}
              </li>
              <li>
                <span className='label' title='Client'>
                  Client:{' '}
                </span>
                {(typeof property?.clientCode === 'object'
                  ? property?.clientCode?.code
                  : property?.clientCode) || 'N/A'}
              </li>
              <li>
                <span className='label' title='State'>
                  State:{' '}
                </span>
                {property?.state || 'N/A'}
              </li>
              <li>
                <span className='label' title='Opened'>
                  Opened:{' '}
                </span>
                {fmtDate(workOrder?.openDate)}
              </li>
              <li>
                <span className='label' title='Due'>
                  Due:{' '}
                </span>
                {fmtDate(workOrder?.dueDate)}
              </li>
              {workOrder?.cancellationDate && (
                <li style={{ backgroundColor: 'red' }}>
                  <span className='label' title='Cancel'>
                    Cancelled:{' '}
                  </span>
                  {fmtDate(workOrder.cancellationDate)}
                </li>
              )}
              <li>
                <span className='label' title='Reassigned'>
                  Reassigned:{' '}
                </span>
                {fmtDate(workOrder?.reassignedDate)}
              </li>

              {/* Audit sidebar question answers */}
              {auditSidebarData.filter(shouldShowWOQuestion).map((q, i) => (
                <li key={i}>
                  <span className='label' title={q.questionText}>
                    {q.questionText}:{' '}
                  </span>
                  <span>
                    {q.answerValues[0]?.length > 60 ? (
                      <span
                        className='cursor-pointer'
                        title={q.answerValues[0]}
                        onClick={() => copy(q.answerValues[0])}
                        style={{ textDecoration: 'dotted' }}
                      >
                        {q.answerValues[0].substring(0, 60)}...
                      </span>
                    ) : (
                      <span className='cursor-pointer' onClick={() => copy(q.answerValues[0])}>
                        {q.answerValues[0]}
                      </span>
                    )}
                  </span>
                </li>
              ))}

              <li>
                <span className='label'>FTV: </span>
                {fmtDate(property?.ftvDate)}
              </li>
              <li>
                <span className='label' title='Work Order Alerts'>
                  SPI WO Alert:
                </span>
                {renderInternalInstructions(workOrder?.internalInstructions)}
              </li>
              {renderSpecialInstructions(
                'Property Special Instructions',
                property?.specialInstructions
              )}
              {renderSpecialInstructions('Special Instructions', workOrder?.specialInstructions)}
              {renderSTData()}
              {renderFinancialWorkflow()}
              {renderVendorWorkflow()}
              {workOrder?.clientOrderNumber && (
                <li>
                  <span className='label' title='Client Order Number'>
                    Client Order #:{' '}
                  </span>
                  {workOrder.clientOrderNumber}
                </li>
              )}
            </ul>
          </div>

          {/* Audit action buttons */}
          <div className='sidebar-group'>
            <div className='pin-profile-group nav-sidebar'>
              <div className='profile-group' style={{ padding: '5px 0' }}>
                {/* WDS shift options dropdown */}
                <AuditShiftOptions countService={countService} />
                {/* Accept All button */}
                <AuditAcceptAllButton />
                {/* Complete Audit button */}
                <AuditCompleteButton countService={countService} />
                {/* Billing modal trigger */}
                <div className='flex justify-center'>
                  <BillingTotalsButton clientTotal={clientTotal} vendorTotal={vendorTotal} />
                </div>
                {/* RCT Call Out button */}
                <AuditRCTButton countService={countService} />
              </div>
            </div>

            {/* Overview link */}
            {countService.totalCount > 0 && (
              <strong className='task-group-heading'>
                <a
                  href='#/Audit/auditdashboard/orderStatusTracker?forceOrderStatusTracker=true'
                  className='overview'
                >
                  <i className='fa fa-arrow-left' /> Overview
                </a>
              </strong>
            )}

            {/* Flagged total */}
            <div className='flagged-total'>
              <span className='notification'>!</span>
              <span>
                {countService.totalCountWithOutNewAudit === 0
                  ? 'No results require action'
                  : countService.totalCountWithOutNewAudit === 1
                    ? '1 Result Requires Action'
                    : `${countService.totalCountWithOutNewAudit} Results Require Action`}
              </span>
            </div>

            {/* Property Condition section */}
            <section className='sidebar-header'>
              <h6>Property Condition</h6>
            </section>
            <ul className='list-results'>
              {countService.questionCountsByCategory.length === 0 ? (
                <li>
                  <span>-- No Property Conditions --</span>
                </li>
              ) : (
                countService.questionCountsByCategory
                  .filter((cat) => !cat.categoryName.includes('- Tasks'))
                  .map((cat, i) => (
                    <li
                      key={i}
                      className={cat.count > 0 ? 'action-required' : 'action-not-required'}
                    >
                      <span className='notification'>{cat.count > 0 ? cat.count : '\u00A0'}</span>
                      <span
                        className='category cursor-pointer'
                        title={cat.categoryName}
                        onClick={() => {
                          const name = cat.categoryName.replace(/\s/g, '');
                          window.location.hash = `#/Audit/auditdashboard/results#${name}`;
                        }}
                      >
                        {cat.categoryName}
                      </span>
                    </li>
                  ))
              )}
            </ul>

            {/* Tasks section */}
            <section className='sidebar-header'>
              <h6>Tasks</h6>
            </section>
            <ul className='list-results'>
              {countService.taskCountsByCategory.length === 0 ? (
                <li>
                  <span>-- No Tasks --</span>
                </li>
              ) : (
                countService.taskCountsByCategory.map((item, i) => (
                  <li key={i}>
                    <span
                      className={`clickable-cell cursor-pointer ${item.requiresAudit ? 'flagged-category' : ''}`}
                      onClick={() => {
                        const name = item.categoryName.replace(/\s/g, '');
                        window.location.hash = `#/Audit/auditdashboard/results#${name}`;
                      }}
                    >
                      <span>
                        {item.count > 0 && <span className='notification'>{item.count}</span>}
                        {item.categoryName}
                      </span>
                    </span>
                  </li>
                ))
              )}
            </ul>

            {/* Damages section */}
            <section className='sidebar-header'>
              <h6>Damages</h6>
            </section>
            <ul className='list-results'>
              {countService.damageCountsByCategory.length === 0 ? (
                <li>
                  <span>-- No Damages --</span>
                </li>
              ) : (
                countService.damageCountsByCategory.map((damage, i) => (
                  <li key={i}>
                    <span
                      className={`clickable-cell cursor-pointer ${!damage.audited ? 'flagged-category' : ''}`}
                      onClick={() => {
                        window.location.hash = '#/Damages/summary';
                      }}
                    >
                      <span>
                        {!damage.audited && <span className='notification'>{damage.count}</span>}
                        {damage.category}
                      </span>
                    </span>
                  </li>
                ))
              )}
            </ul>

            {/* Bids section */}
            <section className='sidebar-header'>
              <h6>Bids</h6>
            </section>
            <ul className='list-results'>
              {countService.bidCountsByCategory.length === 0 ? (
                <li>
                  <span>-- No Bids --</span>
                </li>
              ) : (
                countService.bidCountsByCategory.map((bid, i) => (
                  <li key={i}>
                    <span
                      className={`clickable-cell cursor-pointer ${!bid.audited ? 'flagged-category' : ''}`}
                      onClick={() => {
                        window.location.hash = '#/Bids/summary';
                      }}
                    >
                      <span>
                        {!bid.audited && <span className='notification'>{bid.count}</span>}
                        {bid.category}
                      </span>
                    </span>
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* View Latest Update button */}
          {workOrder?.latestVendorUpdateURL && (
            <div>
              <a className='button whiteButton cursor-pointer' onClick={showVendorUpdate}>
                <span className='fa-stack'>
                  <i className='fa fa-circle-thin fa-stack-2x' />
                  <strong className='fa fa-download fa-stack-1x' />
                </span>
                View Latest Update
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: VM Company Details sidebar (companyDetails / companyMaintenance / serviceArea)
  // ============================================================================

  if (
    currentContext === 'companyDetails' ||
    currentContext === 'companyMaintenance' ||
    currentContext === 'serviceArea'
  ) {
    const company = companyDetails?.company;
    const agData = companyDetails?.agData;

    if (!companyDetails) return <div className='property' />;

    return (
      <div className='property'>
        <div className='sidebar-inner'>
          <div className='sidebar-header'>
            <h4>Company Details</h4>
            <br />
            <ul className='nav nav-sidebar'>
              <li
                className='active cursor-pointer'
                onClick={() => company?.id && copy(String(company.id))}
              >
                <span className='label'>Id </span>
                {company?.id}
              </li>
              <li className='active'>
                <span className='label' title='Tracking Number'>
                  Tracking Number
                </span>
                {company?.archerContentId}
              </li>
              <li className='active'>
                <span className='label'>W9 Business Name </span>
                <div>
                  <a
                    href={`#/VendorManagement/CompanyMaintenance?Search=${company?.id}&SearchBy=CompanyId`}
                    className='linkyellow'
                  >
                    {company?.legalName || ''}
                  </a>
                </div>
              </li>
              <li className='active'>
                <span className='label'>Owner </span>
                <div>{company?.ownerName || ''}</div>
              </li>
              <li>
                <span className='label'>Address </span>
                <div>{company?.address?.address1 || ''}</div>
                <div>{company?.address?.address2 || ''}</div>
              </li>
              <li>
                <span className='label'>City </span>
                {company?.address?.city || ''}
              </li>
              <li>
                <span className='label'>State </span>
                {company?.address?.state || ''}
              </li>
              <li>
                <span className='label'>Zip </span>
                {company?.address?.zipCode || ''}
              </li>
              <li>
                <span className='label'>Phone </span>
                {fmtPhone(company?.phone) || ''}
              </li>
              <li>
                <span className='label'>Phone 2 </span>
                {fmtPhone(company?.phone2) || ''}
              </li>
              <li>
                <span className='label'>Email</span>
                {company?.email && (
                  <a href={`mailto:${company.email}`} className='linkyellow'>
                    {company.email}
                  </a>
                )}
              </li>
              {displayEIN && (
                <li>
                  <span className='label'>EIN</span>
                  {company?.ein}
                </li>
              )}
              <li>
                <span className='label'>Status </span>
                <span
                  style={{
                    color: company?.status?.name === 'Active' ? '#66ff66' : '#ff5b5b',
                  }}
                >
                  {company?.status?.name}
                </span>
              </li>
              <li>
                <span className='label'>Sub-Status </span>
                <span>{company?.subStatus?.name}</span>
              </li>
              <li>
                <span className='label'>Start Date</span>
                <span>{fmtDate(company?.startDate)}</span>
              </li>
              <li>
                <span className='label'>End Date</span>
                <span>{fmtDate(company?.endDate)}</span>
              </li>
              <li>
                <span className='label'>Payments Held</span>
                <span
                  style={{
                    color: company?.isPaymentsOnhold === 1 ? '#ff5b5b' : '#66ff66',
                  }}
                >
                  {company?.isPaymentsOnhold === 1 ? 'Y' : 'N'}
                </span>
              </li>
              <li>
                <span className='label'>Expedited Sign-up </span>
                {company?.isFasttrack === true ? 'Y' : company?.isFasttrack === false ? 'N' : ''}
              </li>
              <li>
                <span className='label'>QS Start Date </span>
                {fmtDate(company?.qsCreateDate)}
              </li>
              <li>
                <span className='label'>QS End Date </span>
                {fmtDate(company?.qsInsuranceEndDate)}
              </li>
              <li>
                <span className='label'>CheckR Status </span>
                {company?.checkrStatus || ''}
              </li>
              <li>
                <span className='label'>CheckR Type </span>
                {company?.checkrType || ''}
              </li>
              <li>
                <span className='label' title='AdminVendor System ID'>
                  Admin Vendor System ID
                </span>
                <div>
                  {!companyDetails.adminVendorSystemId ? (
                    <a
                      href={`#/VendorManagement/SetCompanyAdmin?companyId=${company?.id}&companyName=${company?.legalName}`}
                      className='linkyellow'
                    >
                      Set Vendor System ID
                    </a>
                  ) : (
                    <a
                      href={`#/VendorManagement/SetCompanyAdmin?companyId=${company?.id}&companyName=${company?.legalName}&vendorSystemId=${companyDetails.adminVendorSystemId}`}
                      className='linkyellow'
                    >
                      {companyDetails.adminVendorSystemId}
                    </a>
                  )}
                </div>
              </li>
              <li>
                <span className='label' title='Aspen Grove Org ID'>
                  Aspen Grove Org ID{' '}
                </span>
                {agData?.agOrganizationId || ''}
              </li>
              <li>
                <span className='label' title='Expedited Payment'>
                  Expedited Payment{' '}
                </span>
                {companyDetails.expeditedPayment}
                <div>
                  {!companyDetails.expeditedPayment &&
                    (company?.status?.name === 'Active'
                      ? permissions.exPayment && (
                          <a
                            href={`#/VendorManagement/SetExpeditedPaymentDate?companyId=${company?.id}`}
                            className='linkyellow'
                          >
                            Activate Expedited Payment
                          </a>
                        )
                      : null)}
                </div>
              </li>
              <li>
                <span className='label'>1099 Y/N </span>
                {company?.has1099 === 1 ? 'Y' : 'N'}
              </li>
              <li>
                <span className='label' title='LiabInsExpireDate'>
                  LiabInsExpireDate{' '}
                </span>
                {fmtDate(company?.liabInsExpireDate)}
              </li>
              <li>
                <span className='label' title='EoInsExpireDate'>
                  EoInsExpireDate{' '}
                </span>
                {fmtDate(company?.eoInsExpireDate)}
              </li>
              <li>
                <span className='label' title='Vendor Exception'>
                  Vendor Exception{' '}
                </span>
                {company?.vendorException ? 'Y' : 'N'}
              </li>
              <li>
                <span className='label' title='Exception Expiration Date'>
                  Exception Expire Date{' '}
                </span>
                {fmtDate(company?.exceptionExpirationDate)}
              </li>
              <li>
                <span className='label' title='Registration Date'>
                  Registration Date
                </span>
                {fmtDate(company?.registeredDate)}
                {!company?.registeredDate && company?.status?.name === 'Active' && (
                  <div>
                    <a
                      className='linkyellow cursor-pointer'
                      onClick={() => {
                        // Matches vm.ResendRegistrationEmail
                        if (company?.id) {
                          const vmApiBase = buildApiBase('vmapi');
                          axiosClient
                            .post(
                              `${vmApiBase}/API/VendorManagement/UpdateCompanyResendEmail`,
                              company.id,
                              { headers: { 'Content-Type': 'application/json' } }
                            )
                            .then(() => {
                              showSuccess('Registration email has been resent.');
                            })
                            .catch(() => {
                              showError('Failed to resend registration email.');
                            });
                        }
                      }}
                    >
                      Resend Registration e-mail
                    </a>
                  </div>
                )}
              </li>
              <li>
                <span className='label' title='' />
              </li>
              <li>
                <span className='label' title='COMPANY NOTES'>
                  COMPANY NOTES:
                </span>
                {company?.notes || ''}
              </li>
              <li>
                <span className='label2' title=''>
                  PASSWORD for changes
                  <br />
                  DO NOT CHANGE Contact Info
                </span>
              </li>
            </ul>
          </div>
          <a href='#/VendorManagement/VendorManagement' className='button white return-btn'>
            BACK TO VENDOR MANAGEMENT SEARCH
          </a>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Client Pricing context
  // ============================================================================

  if (currentContext === 'clientpricing') {
    return (
      <div className='property'>
        <div className='sidebar-inner'>
          <a href='#/Admin/Pricing/ChangeSource' className='button white return-btn'>
            Back To Pricing
          </a>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Fallback — empty
  // ============================================================================

  return <div className='property' />;
}

// ============================================================================
// Sub-components (keep sidebar file self-contained)
// ============================================================================

/**
 * Image count display — reads from property store G_WORKORDER_IMAGE_COUNTS.
 *
 * Matches the AngularJS sidebar.html layout: total count + caret toggle, with
 * an expandable list of EIGHT fixed categories (imageType IDs 1-8). Any other
 * imageType returned by /API/Image/GetImageTypeCounts is intentionally
 * excluded — that's why a work order with 36 raw images can still show
 * "Images: 12" (only the Photo bucket has a visible count).
 */
const SIDEBAR_IMAGE_CATEGORIES: { id: string; label: string }[] = [
  { id: '1', label: 'New Order' },
  { id: '2', label: 'Documentation' },
  { id: '3', label: 'Update' },
  { id: '4', label: 'Invoice' },
  { id: '5', label: 'Photo' },
  { id: '6', label: 'Cancel' },
  { id: '7', label: 'BPO/Appraisal' },
  { id: '8', label: 'Title' },
];

function ImageCount() {
  const counts = useWorkOrderImageCounts();
  const [expanded, setExpanded] = useState(false);

  const rows = SIDEBAR_IMAGE_CATEGORIES.map((cat) => ({
    ...cat,
    count: Number(counts?.[cat.id] ?? 0),
  }));
  const total = rows.reduce((sum, r) => sum + r.count, 0);

  if (total === 0) return <span>0</span>;

  return (
    <>
      <span className='cursor-pointer' onClick={() => setExpanded((prev) => !prev)}>
        {total} <i className={`fa fa-caret-${expanded ? 'up' : 'down'}`} />
      </span>
      {expanded && (
        <ul className='nav nav-sidebar pl-[1em]'>
          {rows.map((row) => (
            <li key={row.id}>
              <span className='label'>{row.label}: </span>
              {row.count}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * BillingTotalsButton — Sidebar "C: $X : V: $Y" pill.
 *
 * AngularJS source: layout/sidebar.html line 273:
 *   <a ng-click="toggleAuditBillingModal()" class="button white">
 *     C: {{vm.clientTotal | currency}} : V: {{vm.vendorTotal | currency}}
 *   </a>
 *
 * Uses React hover state + inline styles so neither the global
 * `.button:hover { color:#fff }` rule from main.css nor any sidebar-scoped
 * `button` selector can override the hover color (Tailwind utilities + `!`
 * importants were both being beaten by the legacy cascade in production).
 */
function BillingTotalsButton({
  clientTotal,
  vendorTotal,
}: {
  clientTotal: number;
  vendorTotal: number;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type='button'
      onClick={() => useModalStore.getState().toggle('auditBilling')}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        display: 'inline-block',
        border: '1px solid #fff',
        borderRadius: 9999,
        padding: '0.5em 2em',
        fontSize: '0.8em',
        textTransform: 'uppercase',
        fontWeight: 300,
        background: hover ? '#fff' : 'transparent',
        color: hover ? '#0070cd' : '#fff',
        cursor: 'pointer',
        transition: 'background-color 200ms ease-in, color 200ms ease-in',
        width: '80%',
      }}
    >
      C: ${clientTotal.toFixed(2)} : V: ${vendorTotal.toFixed(2)}
    </button>
  );
}

/** Audit Shift Options dropdown */
// Resolution: incoming store-based wiring kept over HEAD's props-lifting since
// `useSidebarShiftAction` (defined above) already provides the shared signal
// to both AuditShiftOptions (writer) and AuditCompleteButton (reader). Lifted
// props would be redundant.
function AuditShiftOptions({ countService }: { countService: CountServiceState }) {
  const wdsAction = useSidebarShiftAction((s) => s.wdsAction);
  const setWdsAction = useSidebarShiftAction((s) => s.setWdsAction);
  const [shiftOptions, setShiftOptions] = useState<string[]>([]);
  const wds = useWds() as Record<string, any> | null;

  useEffect(() => {
    if (wds?.isWds && wds.ShowShiftOptions) {
      // Load shift options
      import('@/features/auditDashboard/auditApi').then(({ getShiftOptions: getOpts }) => {
        getOpts().then((opts) => setShiftOptions(opts as string[]));
      });
    }
  }, [wds?.isWds, wds?.ShowShiftOptions]);

  if (!wds?.isWds || !wds?.ShowShiftOptions) return null;
  if (countService.totalCount !== 0) return null;

  return (
    <div>
      <select
        className='button white'
        required
        value={wdsAction}
        onChange={(e) => setWdsAction(e.target.value)}
      >
        {shiftOptions.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Accept All button */
function AuditAcceptAllButton() {
  const [, setAccepted] = useState(false);
  const workOrder = usePropertyStore((s) => s.workOrder) as SidebarWorkOrder | null;
  const countService = useCountService();
  const acceptAllSourceSystems = useAcceptAllSourceSystems();

  const canAcceptAll = useCallback(() => {
    if (!workOrder?.workflowStateTransition?.currentState?.editPermission) return false;
    if (validDynamic(workOrder.workflowStateTransition.currentState.editPermission)) {
      // Check source system
      return acceptAllSourceSystems.includes(workOrder.sourceSystem || '');
    }
    return false;
  }, [workOrder, acceptAllSourceSystems]);

  const handleAcceptAll = useCallback(() => {
    const wds = useWdsStore.getState().wds as Record<string, any> | null;

    if (wds?.isWds && wds.itemType !== 'SUBMISSION') {
      broadcastAngularEvent('ACCEPT_ALL_TASKS');
      window.dispatchEvent(new CustomEvent('audit-accept-all-tasks'));
    }
    broadcastAngularEvent('ACCEPT_ALL_QUESTIONS');
    window.dispatchEvent(new CustomEvent('audit-accept-all-questions'));
    setAccepted(true);
  }, []);

  if (!canAcceptAll()) return null;

  return (
    <div>
      <button
        className='button white'
        disabled={countService.questionFailureCount === 0 && countService.taskFailureCount === 0}
        onClick={handleAcceptAll}
      >
        Accept All
      </button>
    </div>
  );
}

/**
 * Port of `determineRedirectAndNextOrder` (sidebar.js:109-132). Runs after a
 * successful Complete Audit API call and routes the user to one of four
 * destinations depending on WDS state and source system. Mirrors AngularJS
 * `$location.url(...).search(...)` via `navigateToLegacyVendorRoute`.
 */
async function determineRedirectAndNextOrder(
  workOrder: SidebarWorkOrder | null,
  gWds: Record<string, unknown> | null,
  wdsAction: string
): Promise<void> {
  const isWds = Boolean(gWds?.isWds);
  const showShiftOptions = Boolean(gWds?.ShowShiftOptions);

  // [WDS-AUDIT-DBG] TEMPORARY diagnostic logging — remove after confirming the
  // "Complete Audit & Continue" bounce root cause.
  console.warn('[WDS-AUDIT-DBG] determineRedirectAndNextOrder', {
    isWds,
    showShiftOptions,
    wdsAction,
    itemTypeRecordId: gWds?.itemTypeRecordId,
    itemType: gWds?.itemType,
    currentWDSQueue: gWds?.currentWDSQueue,
    woOrderNumber: workOrder?.orderNumber,
    sourceSystem: workOrder?.sourceSystem,
  });

  if (isWds && showShiftOptions) {
    // (A) WDS shift mode — fetch next WDS order
    try {
      const itemTypeRecordId = String(gWds?.itemTypeRecordId ?? '');
      const currentWDSQueue = String(gWds?.currentWDSQueue ?? '');
      const result = await getWDSOrder(itemTypeRecordId, 'Completed', wdsAction, currentWDSQueue);
      // Legacy auditDataService.GetWDSOrder resolves with response.data.workOrderResultId
      // (a number) — sidebar.js:114-121 only ever receives the number. The React port
      // was doing `Number(result)` on the WHOLE response object → NaN, so no branch
      // matched and the user was dropped on /Home/dashboard instead of the next order.
      const workOrderResultId = Number(result?.workOrderResultId);

      // [WDS-AUDIT-DBG] TEMPORARY — what did the WDS queue hand back?
      console.warn('[WDS-AUDIT-DBG] getWDSOrder returned', {
        sentItemTypeRecordId: itemTypeRecordId,
        sentStatus: 'Completed',
        sentWdsAction: wdsAction,
        sentQueue: currentWDSQueue,
        rawResult: result,
        workOrderResultId,
        willNavigateTo:
          workOrderResultId > 0
            ? `overview?ItemTypeRecordId=${workOrderResultId}`
            : '/Home/dashboard',
      });

      // Match legacy wds error-code messaging (auditDataService.js:467-475).
      // These land on the dashboard where `wdsMessage` renders as a banner.
      if (workOrderResultId === -1) {
        useWdsStore.getState().setWdsMessage('Queue is empty, please check your queue in WDS.');
      } else if (workOrderResultId === -3) {
        useWdsStore.getState().setWdsMessage('WDS is busy, please try again starting from WDS.');
      } else if (workOrderResultId === -4) {
        useWdsStore
          .getState()
          .setWdsMessage(
            'Current work order is not a match for requested queue of item types. Please check WDS and start over again.'
          );
      } else if (!workOrderResultId) {
        useWdsStore
          .getState()
          .setWdsMessage(
            'Your WDS session has ended, please check your queue in WDS or start over again.'
          );
      }

      if (workOrderResultId > 0) {
        navigateToLegacyVendorRoute('Audit/auditdashboard/overview', {
          ItemTypeRecordId: workOrderResultId,
          Iswds: gWds?.isWds as boolean | undefined,
          CurrentQueue: gWds?.currentWDSQueue as string | undefined,
          ItemType: gWds?.itemType as string | undefined,
        });
      } else {
        navigateToLegacyVendorRoute('/Home/dashboard');
      }
    } catch {
      // graceful — interceptors handle global error toast; skip redirect on failure
    }
  } else if (isWds && !showShiftOptions) {
    // (B) WDS first pass — flip ShowShiftOptions on, then navigate
    useWdsStore.getState().setWds({ ...(gWds || {}), ShowShiftOptions: true });
    navigateToLegacyVendorRoute('/Audit/auditdashboard/results/additionalOrders');
  } else if (workOrder?.sourceSystem !== 'I') {
    // (C) Non-WDS, external source system
    navigateToLegacyVendorRoute('/Audit/auditdashboard/results/additionalOrders');
  } else {
    // (D) Non-WDS, Internal (sourceSystem === 'I')
    navigateToLegacyVendorRoute('Audit/auditdashboard/orderStatusTracker');
  }
}

/** Complete Audit button */
function AuditCompleteButton({ countService }: { countService: CountServiceState }) {
  const workOrder = usePropertyStore((s) => s.workOrder) as SidebarWorkOrder | null;
  const [orderHasBeenAudited, setOrderHasBeenAudited] = useState(false);

  // Re-arm completion whenever a NEW order loads. This button lives in the
  // persistent sidebar, so in a WDS "Complete & Continue" chain it does NOT
  // remount between orders. Without this reset, `orderHasBeenAudited` stays
  // true after the first completion, and on the next order the guard in
  // handleComplete skips completeOrder entirely — the order never gets
  // dispositioned and getWDSOrder re-serves the same order (user gets stuck).
  // The flag still latches within a single order (double-submit protection);
  // only a genuine order change clears it. Audited-status UI is driven by
  // counts/permissions, not this flag, so this does not "un-audit" anything.
  useEffect(() => {
    setOrderHasBeenAudited(false);
  }, [workOrder?.orderNumber]);

  // On the post-completion "Additional Orders" prompt the plain "Complete
  // Audit" button is a dead no-op — clicking it (orderHasBeenAudited already
  // true) just re-navigates to the same page. Grey it out + disable it there
  // (CN-52082). Keyed off the route rather than orderHasBeenAudited/
  // hasPassedAudit so it holds for both WDS and non-WDS — the non-WDS path
  // never sets those flags.
  const location = useLocation();
  const onAdditionalOrdersPage = location.pathname.includes(
    '/Audit/auditdashboard/results/additionalOrders'
  );

  const wds = useWds() as Record<string, any> | null;
  // ...EXCEPT in the WDS queue flow: there the same button becomes "Complete
  // Audit & Continue" and advances to the next order (determineRedirectAndNext
  // Order branch A). That functional state is exactly when the "& Continue" /
  // "& Exit" label shows — wds.isWds && wds.ShowShiftOptions — so only the
  // non-continue (dead) case gets disabled.
  const isDeadCompleteButton = onAdditionalOrdersPage && !(wds?.isWds && wds?.ShowShiftOptions);
  const rctCalloutCalled = useRctStore((s) => s.rctCalloutCalled);
  const rctStateClean = useRctStore((s) => s.rctStateClean);
  const doNotShowRCT = useRctStore((s) => s.doNotShowRct);
  const permissions = useLegacyPermissions();
  // Toasters from HEAD + dropdown-label readout from incoming. Both are kept
  // because they serve different concerns (success/error feedback vs.
  // "& Continue" / "& Exit" suffix in the button label).
  const { showSuccess, showError } = useUIStore();
  const wdsActionLabel = useSidebarShiftAction((s) => s.wdsAction);

  const hasEditPermission = useCallback(() => {
    if (!workOrder?.workflowStateTransition?.currentState?.editPermission) return false;
    return validDynamic(workOrder.workflowStateTransition.currentState.editPermission);
  }, [workOrder]);

  // Merged handler — HEAD's rich logic (success/error toasters, eager
  // workorder refresh on every success path, explicit error branch that
  // re-enables the button) on top of incoming's external
  // `determineRedirectAndNextOrder` helper (defined above) and Zustand-backed
  // `useSidebarShiftAction` for the WDS dropdown value.
  const handleComplete = useCallback(async () => {
    // Exact port of vm.audit.completeAudit (sidebar.js:53-77).
    const gWds = useWdsStore.getState().wds as Record<string, any> | null;
    const wo = usePropertyStore.getState().workOrder as SidebarWorkOrder | null;
    const wdsAction = useSidebarShiftAction.getState().wdsAction;

    // [WDS-AUDIT-DBG] TEMPORARY — state at the moment the Complete button is clicked.
    console.warn('[WDS-AUDIT-DBG] handleComplete ENTRY', {
      orderHasBeenAudited,
      woOrderNumber: wo?.orderNumber,
      itemTypeRecordId: gWds?.itemTypeRecordId,
      itemType: gWds?.itemType,
      isWds: gWds?.isWds,
      showShiftOptions: gWds?.ShowShiftOptions,
      wdsAction,
    });

    // Legacy: `if (vm.orderHasBeenAudited) { determineRedirectAndNextOrder(); return }`
    // — second click while the flag is still set just routes onward without
    // calling CompleteOrder again.
    if (orderHasBeenAudited) {
      // [WDS-AUDIT-DBG] TEMPORARY — guard hit means completeOrder is NOT called.
      console.warn(
        '[WDS-AUDIT-DBG] handleComplete GUARD HIT → completeOrder SKIPPED, redirect only',
        { woOrderNumber: wo?.orderNumber, itemTypeRecordId: gWds?.itemTypeRecordId }
      );
      await determineRedirectAndNextOrder(wo, gWds, wdsAction);
      return;
    }

    setOrderHasBeenAudited(true);

    const id = gWds?.itemTypeRecordId ?? wo?.orderNumber;
    const itemType = (gWds?.itemType ?? null) as string | null;

    if (id == null) {
      // No id to submit — restore the guard so the button stays usable.
      setOrderHasBeenAudited(false);
      showError('No work order loaded — cannot complete audit.');
      return;
    }

    try {
      // [WDS-AUDIT-DBG] TEMPORARY — did completeOrder actually run + succeed?
      console.warn('[WDS-AUDIT-DBG] handleComplete calling completeOrder', { id, itemType });
      const completeResult = await completeOrder(id, itemType);
      console.warn('[WDS-AUDIT-DBG] handleComplete completeOrder RETURNED', { id, completeResult });

      // Refresh the global work-order on EVERY success path (not just the
      // non-WDS branch). The refresh is what populates the new state's
      // editPermission and the isReadOnly flag — both are checked by
      // showButton to hide the button after audit completes, and the gate
      // needs to survive page reload. Legacy gates this on `!isWds` because
      // the WDS branch immediately navigates to a fresh order; in React the
      // sidebar can stay mounted before the navigation lands, so we always
      // refresh to avoid a stale-state window.
      if (wo?.orderNumber != null) {
        try {
          await replaceGlobalOrder(wo.orderNumber);
        } catch {
          // Non-fatal — proceed to redirect even if the refresh fails.
        }
      }

      showSuccess('Audit completed successfully.');

      // Re-read fresh store snapshots — the refresh above may have mutated
      // workOrder and/or wds. Hand them to the external helper which owns
      // the four-branch redirect logic (sidebar.js:109-131).
      await determineRedirectAndNextOrder(
        usePropertyStore.getState().workOrder as SidebarWorkOrder | null,
        useWdsStore.getState().wds as Record<string, any> | null,
        useSidebarShiftAction.getState().wdsAction
      );
    } catch (err) {
      // [WDS-AUDIT-DBG] TEMPORARY — completeOrder threw.
      console.error('[WDS-AUDIT-DBG] handleComplete completeOrder ERROR', { id, err });
      // On failure, release the guard so the user can retry. axiosClient's
      // interceptor already raises a generic toast for 4xx/5xx; we add a more
      // specific one here so the user knows it was the Complete Audit action.
      setOrderHasBeenAudited(false);
      const message = err instanceof Error ? err.message : 'Failed to complete audit.';
      showError(message);
    }
  }, [orderHasBeenAudited, showSuccess, showError]);

  const isSubmission = wds?.itemType === 'SUBMISSION';

  // showButton — exact 1:1 port of legacy ng-if (sidebar.html:393).
  // Permission alone controls visibility. The disabled state below
  // (legacy ng-disabled) handles the "audit already done" UX via counts
  // recomputed against the refreshed workorder.
  const showButton = isSubmission || hasEditPermission();

  if (!showButton) return null;
  // Exact 1:1 port of the legacy ng-class expression on sidebar.html:391.
  // Note: legacy does NOT reference vm.orderHasBeenAudited here — neither the
  // green highlight nor the disable state depend on local click state. The
  // post-complete "hide" is driven entirely by the workorder state changing
  // (see showButton below).
  const isGreen =
    !isDeadCompleteButton &&
    (countService.totalCount === 0 || countService.onFollowupInspectionAllAnsweredActionTaken) &&
    ((permissions.rctManualSubmit && !doNotShowRCT && rctCalloutCalled && rctStateClean) ||
      !permissions.rctManualSubmit ||
      doNotShowRCT);

  // Exact 1:1 port of the legacy ng-disabled expression on sidebar.html:394,
  // PLUS a `!countsLoaded` gate that closes the React-only race window where
  // a hard reload paints the sidebar before getResultsList resolves. In that
  // window the Zustand defaults give `totalCount === 0` so the legacy clause
  // alone reads "not disabled" and the button briefly looks ready to click.
  // AngularJS's digest cycle doesn't expose this gap the same way, so legacy
  // doesn't need the extra gate. `countsLoaded` flips to true the moment
  // refreshQuestionCounts / refreshQuestionCountsV2 / refreshTaskCounts runs.
  const isDisabled =
    isDeadCompleteButton ||
    !countService.countsLoaded ||
    (countService.totalCount > 0 && !countService.onFollowupInspectionAllAnsweredActionTaken) ||
    (Boolean(permissions.rctManualSubmit) &&
      !doNotShowRCT &&
      (!rctCalloutCalled || !rctStateClean));

  return (
    <div>
      <button
        // `button white` already has legacy main.css :disabled styling (border
        // + color → #acacac, hover bg #555). The Tailwind disabled: utilities
        // are a fallback for environments where main.css isn't loaded.
        className={`button white ${isGreen ? 'greenButton' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
        disabled={isDisabled}
        onClick={handleComplete}
      >
        Complete Audit
        {wds?.isWds && wds?.ShowShiftOptions && (
          // sidebar.html:396-397 — label reflects the selected vm.wdsAction
          <span>{wdsActionLabel === 'Continue' ? ' & Continue' : ' & Exit'}</span>
        )}
      </button>
    </div>
  );
}

/** RCT Call Out button */
function AuditRCTButton({ countService }: { countService: CountServiceState }) {
  const permissions = useLegacyPermissions();
  const doNotShowRCT = useRctStore((s) => s.doNotShowRct);
  const rctAlwaysEnable = useRctStore((s) => s.rctAlwaysEnable);
  const { showSuccess, showError } = useUIStore();

  if (!permissions.rctManualSubmit) return null;
  if (doNotShowRCT) return null;

  const isGreen =
    rctAlwaysEnable ||
    countService.totalCount === 0 ||
    countService.onFollowupInspectionAllAnsweredActionTaken;

  const isDisabled =
    !rctAlwaysEnable &&
    countService.totalCount > 0 &&
    !countService.onFollowupInspectionAllAnsweredActionTaken;

  const handleValuate = async () => {
    const { valuateOrder } = await import('@/features/auditDashboard/auditApi');
    const workOrder = usePropertyStore.getState().workOrder;
    if (!workOrder?.orderNumber) return;

    try {
      // 1:1 with AngularJS vm.audit.valuateOrder (sidebar.js:92-106). The
      // GetRCTOrderValuation endpoint returns HTTP 200 for both outcomes with a
      // { success, message } body — so the whole block runs on resolve, whether
      // success is true (e.g. RCT created) or false (e.g. "Farmers RCT Not
      // Created"). AngularJS has no .catch, so a transport-level error leaves
      // state untouched (no toast, no reload) — the catch below mirrors that.
      const res = await valuateOrder(Number(workOrder.orderNumber));
      useRctStore.getState().setRctCalloutCalled(true);
      if (res?.success === true) {
        showSuccess('Success, RCT Created and Saved in SafeView');
      } else {
        showError(res?.message);
      }
      reloadAuditResults();
      useRctStore.getState().setRctStateClean(true);
    } catch {
      // graceful degradation — matches AngularJS (no .catch on the promise)
    }
  };

  return (
    <div>
      <button
        className={`button white ${isGreen ? 'greenButton' : ''}`}
        disabled={isDisabled}
        onClick={handleValuate}
      >
        RCT CALL OUT
      </button>
    </div>
  );
}

export default AppSidebar;
